import React, { useEffect, useState } from 'react'
import BoardComponent, { IBoardView } from './Board'
import EditBoardComponent from './EditBoard'
import BoardStore, {
    EventsEnum as BoardEvents,
} from '../../flux/boards/BoardStore'
import { updateBoard, deleteBoard } from '../../flux/boards/BoardActions'
import swimlaneStore, {
    EventsEnum as SwimlaneEvents,
} from '../../flux/swimlanes/SwimlaneStore'
import DeleteBoardModalComponent from './DeleteBoardModal'
import SettingsStore from '../../flux/settings/SettingsStore'
import { enableFocus, disableFocus } from '../../flux/settings/SettingsActions'

/**
 * Properties expected by the control board component
 */
export interface IControlBoardProps {
    boardId: number
    canMove: boolean
    canFocus: boolean
    isFocused: boolean
    isHidden: boolean
    onMove: (boardId: number) => void
}

/**
 * Controller Component for Board.
 * Loads the board from the board store.
 *
 * Determines if we are in Edit or Show mode for the board.
 */
function ControlBoardComponent(props: IControlBoardProps) {
    const settingsStore = SettingsStore.GetInstance()
    const [board, setBoard] = useState(BoardStore.getBoard(props.boardId))
    const [isEdit, setEdit] = useState(false)
    const [isDelete, setIsDelete] = useState(false)

    /**
     * Expresses the IBoard as an IBoardView instance
     * @returns {IBoardView} board as IBoardView
     */
    function getView(): IBoardView {
        return {
            id: board.id,
            order: board.order,
            name: board.name,
            swimlaneIds: board.swimlanes.map((x) => x.id),
        }
    }

    /**
     * Invoked when in edit mode if edit mode was cancelled
     */
    function handleEditCancel() {
        setEdit(false)
    }

    /**
     * Invoked when save raised from edit component.
     * Updates the board in the store
     * @param board Board to update
     */
    function handleSave(board: IBoardView) {
        updateBoard(board)
        setEdit(false)
    }

    /**
     * Invoked when user wants to edit the board
     */
    function handleEdit() {
        setEdit(true)
    }

    /**
     * Handles the request to delete this swimlane
     */
    function handleDeleteRequest() {
        if (board.swimlanes.length === 0) {
            disableFocus()
            deleteBoard(board)
            return
        }
        // update board from store and set delete
        setBoard(BoardStore.getBoard(props.boardId))
        setIsDelete(true)
    }

    /**
     * Invoked when the user clicks on "Cancel"
     * from the delete confirm dialog.
     */
    function handleDeleteCancel() {
        setIsDelete(false)
    }

    /**
     * Invoked when the user clicks on "Confirm"
     * from the delete confirm dialog.
     */
    function handleConfirm() {
        disableFocus()
        deleteBoard(board)
        setIsDelete(false)
    }

    /**
     * Invoked when the user clicks on the "Focus" toggle.
     * Enabled / Disables focus mode.
     */
    function handleFocus() {
        if (settingsStore.Focus.isFocused) disableFocus()
        else enableFocus(props.boardId)
    }

    useEffect(() => {
        /**
         * Invoked if a swimlane is added or deleted from a board.
         * Determines if this component should re-render
         * @param swimlaneId ID of swimlane which was changed
         */
        function handleSwimlaneUpdate(boardId: number, swimlaneId: number) {
            if (board.id !== boardId) return
            setBoard(BoardStore.getBoard(props.boardId))
        }

        /**
         * Invoked when a board is updated in the Board Store.
         * Updates this view if it's rendering the board which was updated
         * @param boardId ID of Board
         */
        function handleBoardEdit(boardId: number) {
            if (board.id !== boardId) return
            setBoard(BoardStore.getBoard(props.boardId))
        }

        swimlaneStore.addSwimlaneStoreListener(
            SwimlaneEvents.ADD,
            handleSwimlaneUpdate
        )
        swimlaneStore.addSwimlaneStoreListener(
            SwimlaneEvents.DELETE,
            handleSwimlaneUpdate
        )
        swimlaneStore.addSwimlaneStoreListener(
            SwimlaneEvents.MOVE,
            handleSwimlaneUpdate
        )
        BoardStore.addBoardStoreListener(BoardEvents.EDIT, handleBoardEdit)
        return () => {
            swimlaneStore.removeSwimlaneStoreListener(
                SwimlaneEvents.ADD,
                handleSwimlaneUpdate
            )
            swimlaneStore.removeSwimlaneStoreListener(
                SwimlaneEvents.DELETE,
                handleSwimlaneUpdate
            )
            swimlaneStore.removeSwimlaneStoreListener(
                SwimlaneEvents.MOVE,
                handleSwimlaneUpdate
            )
            BoardStore.removeBoardStoreListener(
                BoardEvents.EDIT,
                handleBoardEdit
            )
        }
    }, [board, props.boardId])

    return (
        <>
            {isDelete && (
                <DeleteBoardModalComponent
                    board={board}
                    onCancel={handleDeleteCancel}
                    onConfirm={handleConfirm}
                />
            )}
            {isEdit && (
                <EditBoardComponent
                    board={getView()}
                    onCancel={handleEditCancel}
                    onSave={handleSave}
                />
            )}
            {!isEdit && (
                <BoardComponent
                    board={getView()}
                    canMove={props.canMove}
                    canFocus={props.canFocus}
                    onMove={() => props.onMove(props.boardId)}
                    onEdit={handleEdit}
                    onDelete={handleDeleteRequest}
                    isFocused={props.isFocused}
                    onFocus={handleFocus}
                    isHidden={props.isHidden}
                />
            )}
        </>
    )
}

export default ControlBoardComponent
